import { http } from "../lib/http"

/**
 * Auth.js v5 client for the RAM-OS engine.
 *
 * Auth.js exposes form-urlencoded endpoints under `/api/auth/*`. Every
 * mutating call must include a CSRF token fetched from
 * `/api/auth/csrf`; that endpoint also sets the matching
 * double-submit cookie. With `credentials: "include"` (via `http()`
 * and the raw `fetch` used for form POSTs) the cookie travels back
 * on subsequent calls automatically.
 *
 * Runtime validation of the session payload is hand-rolled because
 * zod is introduced by PR #46 (Session C) and is not yet present on
 * `main`. When #46 lands, this can be swapped for a zod schema in a
 * three-line follow-up.
 */

const DEFAULT_BASE_URL = "http://localhost:9171"

const envBase = import.meta.env.VITE_ENGINE_BASE_URL
const rawBase = typeof envBase === "string" && envBase.length > 0 ? envBase : DEFAULT_BASE_URL
const baseUrl = rawBase.endsWith("/") ? rawBase.slice(0, -1) : rawBase

export interface SessionUser {
	id?: string
	email?: string
	name?: string | null
	image?: string | null
}

export interface Session {
	user: SessionUser
	expires: string
	/**
	 * Role names assigned to the user, surfaced at the top level of the
	 * Auth.js session JSON by the engine's session callback. Empty array
	 * only when the field is absent; reject the whole session if it's
	 * present-but-malformed (sibling fields use the same defensive style).
	 */
	roles: readonly string[]
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null

const validateSession = (raw: unknown): Session | null => {
	if (!isObject(raw)) return null
	const { user, expires, roles } = raw
	if (typeof expires !== "string") return null
	if (!isObject(user)) return null
	const { id, email, name, image } = user
	if (id !== undefined && typeof id !== "string") return null
	if (email !== undefined && typeof email !== "string") return null
	if (name !== undefined && name !== null && typeof name !== "string") return null
	if (image !== undefined && image !== null && typeof image !== "string") return null
	const validatedUser: SessionUser = {}
	if (typeof id === "string") validatedUser.id = id
	if (typeof email === "string") validatedUser.email = email
	if (name === null || typeof name === "string") validatedUser.name = name
	if (image === null || typeof image === "string") validatedUser.image = image
	let validatedRoles: readonly string[] = []
	if (roles !== undefined) {
		if (!Array.isArray(roles)) return null
		if (!roles.every((r) => typeof r === "string")) return null
		validatedRoles = roles
	}
	return { user: validatedUser, expires, roles: validatedRoles }
}

export const getCsrfToken = async (): Promise<string> => {
	const raw = await http<unknown>("/api/auth/csrf")
	if (!isObject(raw) || typeof raw.csrfToken !== "string") {
		throw new Error("Auth: missing csrfToken in /api/auth/csrf response")
	}
	return raw.csrfToken
}

const postForm = async (path: string, body: Record<string, string>): Promise<void> => {
	const params = new URLSearchParams(body)
	const res = await fetch(`${baseUrl}${path}`, {
		method: "POST",
		credentials: "include",
		// `X-Auth-Return-Redirect` makes @auth/core answer with a 200 + JSON
		// `{ url }` (carrying the session-clearing/-setting Set-Cookie) instead of
		// a 302. Without it the default `redirect: "follow"` fetch chases the 302
		// to a cross-origin / http:// Location behind the reverse proxy + tunnel
		// and rejects with "Failed to fetch" — so e.g. sign-out never reaches its
		// mutation's onSuccess and the UI stays signed in. `redirect: "manual"` is
		// belt-and-braces against any stray 3xx. (Same fix as otp.ts `verifyOtp`.)
		redirect: "manual",
		headers: {
			"Content-Type": "application/x-www-form-urlencoded",
			Accept: "application/json",
			"X-Auth-Return-Redirect": "1",
		},
		body: params.toString(),
	})
	if (!res.ok) {
		throw new Error(`Auth: POST ${path} failed (HTTP ${res.status})`)
	}
}

export const signInWithEmail = async (email: string): Promise<void> => {
	const csrfToken = await getCsrfToken()
	await postForm("/api/auth/signin/email", {
		email,
		csrfToken,
		callbackUrl: "/",
		json: "true",
	})
}

export const signOut = async (): Promise<void> => {
	const csrfToken = await getCsrfToken()
	await postForm("/api/auth/signout", {
		csrfToken,
		callbackUrl: "/",
		json: "true",
	})
}

export const getSession = async (): Promise<Session | null> => {
	const res = await fetch(`${baseUrl}/api/auth/session`, {
		method: "GET",
		credentials: "include",
		headers: { Accept: "application/json" },
	})
	if (res.status === 204) return null
	if (!res.ok) return null
	const text = await res.text()
	if (text.length === 0) return null
	let parsed: unknown
	try {
		parsed = JSON.parse(text)
	} catch {
		return null
	}
	// Auth.js returns `{}` when there is no active session.
	if (isObject(parsed) && Object.keys(parsed).length === 0) return null
	return validateSession(parsed)
}
